import React, { useEffect, useState } from "react";
import {
    Dialog,
    Grid,
    Box,
    Typography,
    IconButton,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useForm } from "react-hook-form";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { useCodetablesSlice, getCodeTableValuesByTableName } from "../../../redux/slice/codetableSlice";
import { fetchTemplatesByTypeData, usePracticeEditSlice, addPracticeTemplateData, editPracticeTemplateData, fetchManufacturerTemplatesData, addManufacturerTemplateData, editManufacturerTemplateData } from "../../../redux/slice/practice/practiceEditSlice";
import { setSingleAlertObj, SingleAlertInfo, fetchManufacturersList, useCommonReducer } from "../../../redux/slice/commonSlice";
import { usePracticeProductsSlice } from "../../../redux/slice/practice/practiceProductSlice";
import { useProductListReducer } from "../../../redux/slice/product/productListSlice";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { Close, EditOutlined } from "@mui/icons-material";
import CustomHelpMessage from "../../common/CustomHelpMessage";
import { useModalStyles } from "../../common/useModalStyles";
import { FileText } from "lucide-react";
import { FieldLabel } from "../../../utils/Utility";

export interface PracticeTemplateFormData {
    manufacturerId?: string;
    templateType: string;
    templateId: string;
}

export interface EditPracticeTemplateData {
    manufacturerPracticeTemplateId?: number;
    manufacturerId?: string;
    templateType: string;
    templateId: string;
}

interface PracticeTemplatePopupProps {
    open: boolean;
    onClose: () => void;
    onAdd?: (data: PracticeTemplateFormData) => void;
    onSuccess?: () => void;
    practiceId?: string;
    manufacturerId?: string;
    editData?: EditPracticeTemplateData;
    isEditMode?: boolean;
    mode?: 'practice' | 'manufacturer';
}

const PracticeTemplatePopup: React.FC<PracticeTemplatePopupProps> = ({
    open,
    onClose,
    onAdd,
    onSuccess,
    practiceId,
    manufacturerId,
    editData,
    isEditMode = false,
    mode = 'practice',
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {
        tokens,
        paperSx,
        headerSx,
        iconBoxSx,
        closeIconSx,
        bodySx,
        footerSx,
        fieldWrapSx,
        primaryBtnSx,
        cancelBtnSx,
    } = useModalStyles();
    const { allCodeTablesValuesMap } = useCodetablesSlice();
    const { templatesByTypeData, manufacturerTemplatesData, practiceTemplateData } = usePracticeEditSlice();
    const { manufacturerInfoList } = useCommonReducer();
    const { practiceProductsInfo } = usePracticeProductsSlice();
    const { productsByUserRole } = useProductListReducer();

    const [manufacturerOptions, setManufacturerOptions] = useState<any[]>([]);
    const [templateTypeOptions, setTemplateTypeOptions] = useState<any[]>([]);
    const [templateOptions, setTemplateOptions] = useState<any[]>([]);

    const { control, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<PracticeTemplateFormData>({
        defaultValues: {
            manufacturerId: mode === 'practice' ? "" : undefined,
            templateType: "",
            templateId: "",
        },
    });

    useEffect(() => {
        if (isEditMode && editData && open) {
            setValue("templateType", editData.templateType);
            setValue("templateId", editData.templateId);
            if (mode === 'practice' && editData.manufacturerId) {
                setValue("manufacturerId", editData.manufacturerId);
            }
        } else if (!isEditMode && open) {
            reset();
        }
    }, [isEditMode, editData, open, setValue, reset, mode]);

    const selectedTemplateType = watch("templateType");
    const selectedManufacturerId = watch("manufacturerId");

    useEffect(() => {
        if (open && mode === 'practice' && manufacturerInfoList.length === 0) {
            dispatch(fetchManufacturersList());
        }
    }, [open, mode, dispatch, manufacturerInfoList.length]);

    useEffect(() => {
        if (allCodeTablesValuesMap) {
            let templateTypes = getCodeTableValuesByTableName(allCodeTablesValuesMap, "TemplateType");

            // Exclude template types that are present in manufacturer templates if manufacturer is selected
            // if (mode === 'practice' && selectedManufacturerId && manufacturerTemplatesData && manufacturerTemplatesData.length > 0   ) {
            //     const typeSet = new Set(manufacturerTemplatesData.map(template => template.type));
            //     const typesToExclude = Array.from(typeSet);
            //     templateTypes = templateTypes?.filter(type => !typesToExclude.includes(type.keyName)) || [];
            // }

            setTemplateTypeOptions(templateTypes || []);
        }
    }, [allCodeTablesValuesMap]);

    // Handle manufacturer selection change
    useEffect(() => {
        if (mode === 'practice' && selectedManufacturerId && selectedManufacturerId.trim() !== "" && !isEditMode) {
            dispatch(fetchManufacturerTemplatesData(selectedManufacturerId));
            // Clear template type selection when manufacturer changes
            setValue("templateType", "");
            setValue("templateId", "");
        } else if (mode === 'practice' && !selectedManufacturerId) {
            // Reset template type options to all available when no manufacturer is selected
            setValue("templateType", "");
            setValue("templateId", "");
        }
    }, [selectedManufacturerId, dispatch, setValue, mode]);

    useEffect(() => {
        if (manufacturerInfoList && manufacturerInfoList.length > 0 && 
            practiceProductsInfo && practiceProductsInfo.length >= 0 && 
            productsByUserRole && productsByUserRole.length > 0) {
            
            const practiceProductIds = practiceProductsInfo.map(product => product.productId);
            
            const practiceManufacturerIds = new Set<number>();
            productsByUserRole.forEach(product => {
                if (practiceProductIds.includes(product.productId)) {
                    if (product.manufacturerId) {
                        practiceManufacturerIds.add(product.manufacturerId);
                    }
                }
            });

            // Filter manufacturers based on:
            // 1. Their products are used by the practice
            // 2. They have template_level config as "Practice"
            const filteredManufacturers = manufacturerInfoList.filter((manufacturer: any) => {
                const hasTemplateLevel = manufacturer.customConfig && manufacturer.customConfig.template_level === "Practice";
                const hasProductsInPractice = practiceManufacturerIds.has(manufacturer.manufacturerId);
                return hasTemplateLevel && hasProductsInPractice;
            });
            
            setManufacturerOptions(filteredManufacturers);

            if (isEditMode && editData && editData.manufacturerId && filteredManufacturers.length > 0) {
                const manufacturerExists = filteredManufacturers.some(m => m.manufacturerId.toString() === editData.manufacturerId);
                if (manufacturerExists) {
                    setValue("manufacturerId", editData.manufacturerId);
                }
            }
        } else if (manufacturerInfoList && manufacturerInfoList.length > 0) {
            // Fallback: if practice products or products by user role are not available,
            // show all manufacturers with template_level "Practice"
            const filteredManufacturers = manufacturerInfoList.filter((manufacturer: any) => {
                return manufacturer.customConfig && manufacturer.customConfig.template_level === "Practice";
            });
            setManufacturerOptions(filteredManufacturers);

            if (isEditMode && editData && editData.manufacturerId && filteredManufacturers.length > 0) {
                const manufacturerExists = filteredManufacturers.some(m => m.manufacturerId.toString() === editData.manufacturerId);
                if (manufacturerExists) {
                    setValue("manufacturerId", editData.manufacturerId);
                }
            }
        }
    }, [manufacturerInfoList, practiceProductsInfo, productsByUserRole, isEditMode, editData?.manufacturerId, setValue]);

    useEffect(() => {
        if (selectedTemplateType && selectedTemplateType.trim() !== "") {
            dispatch(fetchTemplatesByTypeData(selectedTemplateType));
            if (!isEditMode || !editData) {
                setValue("templateId", "");
            }
        } else {
            setTemplateOptions([]);
            setValue("templateId", "");
        }
    }, [selectedTemplateType, dispatch, setValue, isEditMode, editData]);

    useEffect(() => {
        if (templatesByTypeData) {
            const templates = Array.isArray(templatesByTypeData) ? templatesByTypeData : [templatesByTypeData];
            setTemplateOptions(templates);

            if (isEditMode && editData && editData.templateId && templates.length > 0) {
                const templateExists = templates.some(t => t.templateId.toString() === editData.templateId);
                if (templateExists) {
                    setValue("templateId", editData.templateId);
                }
            }
        } else {
            setTemplateOptions([]);
        }
    }, [templatesByTypeData, isEditMode, editData?.templateId, setValue]);

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit = async (data: PracticeTemplateFormData) => {
        if (mode === 'practice' && !practiceId) {
            return;
        }
        if (mode === 'manufacturer' && !manufacturerId) {
            return;
        }

        // Check for duplicate template types when adding new templates
        if (!isEditMode) {
            if (mode === 'practice') {
                const existingActiveTemplate = practiceTemplateData?.find(template =>
                    template.type === data.templateType &&
                    template.manufacturerId?.toString() === data.manufacturerId &&
                    !template.inactiveDate
                );

                if (existingActiveTemplate) {
                    const errorObj: SingleAlertInfo = {
                        message: `A template of type "${data.templateType}" already exists for this practice. Please edit the existing template instead of adding a new one.`,
                        alertType: "warning",
                    };
                    dispatch(setSingleAlertObj(errorObj));
                    return;
                }
            } else if (mode === 'manufacturer') {
                const existingActiveTemplate = manufacturerTemplatesData?.find(template =>
                    template.type === data.templateType &&
                    !template.inactiveDate
                );

                if (existingActiveTemplate) {
                    const errorObj: SingleAlertInfo = {
                        message: `A template of type "${data.templateType}" already exists for this manufacturer. Please edit the existing template instead of adding a new one.`,
                        alertType: "warning",
                    };
                    dispatch(setSingleAlertObj(errorObj));
                    return;
                }
            }
        }

        try {
            let result;
            let successMessage;
            let errorMessage;

            if (mode === 'practice') {
                if (isEditMode && editData) {
                    const editRequestData = {
                        manufacturerPracticeTemplateId: editData.manufacturerPracticeTemplateId!,
                        practiceId: parseInt(practiceId!),
                        templateId: parseInt(data.templateId),
                        manufacturerId: parseInt(data.manufacturerId!),
                        templateLevel: "Practice"
                    };
                    result = await dispatch(editPracticeTemplateData(editRequestData));
                    successMessage = "Practice Template Successfully Updated";
                    errorMessage = "Failed to update practice template. Please try again.";
                } else {
                    const addRequestData = {
                        practiceId: parseInt(practiceId!),
                        templateId: parseInt(data.templateId),
                        manufacturerId: parseInt(data.manufacturerId!),
                        templateLevel: "Practice"
                    };
                    result = await dispatch(addPracticeTemplateData(addRequestData));
                    successMessage = "Practice Template Successfully Added";
                    errorMessage = "Failed to add practice template. Please try again.";
                }

                const isSuccess = isEditMode
                    ? editPracticeTemplateData.fulfilled.match(result)
                    : addPracticeTemplateData.fulfilled.match(result);

                if (isSuccess) {
                    const successObj: SingleAlertInfo = {
                        message: successMessage,
                        alertType: "success",
                    };
                    dispatch(setSingleAlertObj(successObj));

                    if (onSuccess) {
                        onSuccess();
                    }

                    if (onAdd) {
                        onAdd(data);
                    }

                    handleClose();
                } else {
                    const errorObj: SingleAlertInfo = {
                        message: errorMessage,
                        alertType: "error",
                    };
                    dispatch(setSingleAlertObj(errorObj));
                }
            } else {
                if (isEditMode && editData) {
                    const editRequestData = {
                        manufacturerPracticeTemplateId: editData.manufacturerPracticeTemplateId!,
                        manufacturerId: parseInt(manufacturerId!),
                        templateId: parseInt(data.templateId),
                        templateLevel: "Manufacturer"
                    };
                    result = await dispatch(editManufacturerTemplateData(editRequestData));
                    successMessage = "Manufacturer Template Successfully Updated";
                    errorMessage = "Failed to update manufacturer template. Please try again.";
                } else {
                    const addRequestData = {
                        manufacturerId: parseInt(manufacturerId!),
                        templateId: parseInt(data.templateId),
                        templateLevel: "Manufacturer"
                    };
                    result = await dispatch(addManufacturerTemplateData(addRequestData));
                    successMessage = "Manufacturer Template Successfully Added";
                    errorMessage = "Failed to add manufacturer template. Please try again.";
                }
                
                const isSuccess = isEditMode
                    ? editManufacturerTemplateData.fulfilled.match(result)
                    : addManufacturerTemplateData.fulfilled.match(result);

                if (isSuccess) {
                    const successObj: SingleAlertInfo = {
                        message: successMessage,
                        alertType: "success",
                    };
                    dispatch(setSingleAlertObj(successObj));

                    if (onSuccess) {
                        onSuccess();
                    }

                    if (onAdd) {
                        onAdd(data);
                    }

                    handleClose();
                } else {
                    const errorObj: SingleAlertInfo = {
                        message: errorMessage,
                        alertType: "error",
                    };
                    dispatch(setSingleAlertObj(errorObj));
                }
            }
        } catch (error) {
            const entityType = mode === 'practice' ? 'practice' : 'manufacturer';
            const errorObj: SingleAlertInfo = {
                message: `An error occurred while ${isEditMode ? 'updating' : 'adding'} the ${entityType} template. Please try again.`,
                alertType: "error",
            };
            dispatch(setSingleAlertObj(errorObj));
        }
    };

    const manufacturerOptionsFormatted = manufacturerOptions.map(item => ({
        label: item.manufacturerName,
        value: item.manufacturerId.toString()
    }));

    const templateTypeOptionsFormatted = templateTypeOptions.map(item => ({
        label: item.displayValue,
        value: item.keyName
    }));



    const templateOptionsFormatted = Array.isArray(templateOptions) ? templateOptions.map(item => ({
        label: item.templateName || item.templateFileName || item.fileName || `Template ${item.templateId}`,
        value: item.templateId.toString()
    })) : [];

    return (
        <Dialog
            open={open}
            keepMounted
            fullWidth
            onClose={handleClose}
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{ sx: paperSx(isMobile) }}
            onClick={(event) => {
                event.stopPropagation();
            }}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={headerSx}>
                    <Box sx={iconBoxSx(tokens.accent)}>
                        <FileText size={20} color={tokens.accent} strokeWidth={1.8} />
                    </Box>
                    <Box sx={{ flex: 1, pr: 4 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography fontWeight={700} sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                                {isEditMode
                                    ? `Edit ${mode === 'practice' ? 'Practice' : 'Manufacturer'} Template`
                                    : `Add ${mode === 'practice' ? 'Practice' : 'Manufacturer'} Template`
                                }
                            </Typography>
                            {/* CS-1518 - Added tooltip help message */}
                            {mode === 'practice' && (
                                <CustomHelpMessage
                                    title="Help"
                                    message="Only manufacturers who have set their “Template Level” to “Practice” in Manufacturer Settings and have active agreement(s) with this practice will be available for selection." />
                            )}
                        </Box>
                        <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                            Select template source, type, and template file.
                        </Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
                        <Close sx={{ fontSize: "1rem" }} />
                    </IconButton>
                </Box>
                <Box sx={bodySx}>
                    <Grid container spacing={2}>
                        {mode === 'practice' && (
                            <Grid item md={6} xs={12}>
                                <Box>
                                    <FieldLabel label="Manufacturer" required tokens={tokens} />
                                <Box sx={fieldWrapSx(!!errors?.manufacturerId)}>
                                    <CustomDropdownField
                                        name="manufacturerId"
                                        control={control}
                                        options={manufacturerOptionsFormatted}
                                        label=""
                                        rules={{ required: "Manufacturer is required." }}
                                    />
                                </Box>
                                </Box>
                            </Grid>
                        )}
                        <Grid item md={mode === 'practice' ? 6 : 12} xs={12}>
                            <Box>
                                <FieldLabel label="Template Type" required tokens={tokens} />
                            <Box sx={fieldWrapSx(!!errors?.templateType)}>
                                <CustomDropdownField
                                    name="templateType"
                                    control={control}
                                    options={templateTypeOptionsFormatted}
                                    label=""
                                    rules={{ required: "Template Type is required." }}
                                />
                            </Box>
                            </Box>
                        </Grid>
                        <Grid item md={12} xs={12}>
                            <Box>
                                <FieldLabel label="Template" required tokens={tokens} />
                            <Box sx={fieldWrapSx(!!errors?.templateId)}>
                                <CustomDropdownField
                                    name="templateId"
                                    control={control}
                                    options={templateOptionsFormatted}
                                    label=""
                                    rules={{ required: "Template is required." }}
                                />
                            </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
                <Box sx={footerSx}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1.5,
                            flexDirection: { xs: "column", sm: "row" },
                        }}
                    >
                        <Box
                            component="button"
                            type="submit"
                            sx={{
                                ...primaryBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.8,
                                px: 3,
                                py: 1.2,
                                border: "none",
                                cursor: "pointer",
                                color: "#fff",
                                order: { xs: 1, sm: 2 },
                            }}
                        >
                            <EditOutlined sx={{ fontSize: "1.1rem" }} />
                            {isEditMode ? "Update" : "Add"}
                        </Box>
                        <Box
                            component="button"
                            type="button"
                            onClick={handleClose}
                            sx={{
                                ...cancelBtnSx,
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.8,
                                px: 3,
                                py: 1.2,
                                border: "1px solid",
                                cursor: "pointer",
                                order: { xs: 2, sm: 1 },
                            }}
                        >
                            Cancel
                        </Box>
                    </Box>
                </Box>
            </form>
        </Dialog>
    );
};

export default PracticeTemplatePopup;